import Popover from '@mui/material/Popover';
import type { TAvailableTag } from '@onesource/schemas';
import { useGetAvailableTags } from '@onesource/services';
import { useMemo, useState, type FC, type MouseEvent } from 'react';
import { MdOutlineLocalOffer } from 'react-icons/md';

import { TAG_PICKER_PAPER_SX, TagPickerPanel } from '../tag-picker-panel';
import { FilterTriggerButton } from './filter-trigger-button';

type Props = {
  /** Sub-space id — the API walks up to the container and returns its tags */
  spaceId: string;
  /** Selected available-tag values (question tag id or subspace/lesson source id) */
  value?: string[];
  onChange: (value: string[] | undefined) => void;
};

/**
 * Multi-select tag filter — searchable dropdown of the space's available tags
 * (question topics, content modules, lessons). The panel is shared with the
 * post composer's tag picker so the two look identical. Posts matching ANY
 * selected tag pass (server-side).
 */
export const TagFilter: FC<Props> = ({ spaceId, value, onChange }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const selected = value ?? [];

  // Lazy-load: only fetch once the dropdown opens, or when a selection came in
  // from the URL and the button needs its label. `usedOnly` hides tags no post
  // carries yet — filtering by them would only ever yield an empty feed.
  const { data: availableTags, isLoading } = useGetAvailableTags(spaceId, {
    enabled: !!anchorEl || selected.length > 0,
    usedOnly: true,
  });

  const options = useMemo(() => availableTags ?? [], [availableTags]);
  const selectedOptions = useMemo(() => options.filter(o => value?.includes(o.value)), [options, value]);

  const handleSelect = (tags: TAvailableTag[]) => {
    onChange(tags.length > 0 ? tags.map(t => t.value) : undefined);
  };

  const selectedLabel =
    selected.length === 1
      ? (selectedOptions[0]?.label ?? 'Tag')
      : selected.length > 1
        ? `${selected.length} selected`
        : undefined;

  return (
    <>
      <FilterTriggerButton
        label="Tags"
        icon={<MdOutlineLocalOffer size={15} />}
        value={selectedLabel}
        onClick={(e: MouseEvent<HTMLButtonElement>) => setAnchorEl(e.currentTarget)}
      />
      <Popover
        anchorEl={anchorEl}
        open={!!anchorEl}
        disableAutoFocus
        disableEnforceFocus
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{ paper: { sx: TAG_PICKER_PAPER_SX } }}
      >
        <TagPickerPanel
          title="Filter by tags"
          options={options}
          value={selectedOptions}
          onChange={handleSelect}
          loading={isLoading}
        />
      </Popover>
    </>
  );
};
